import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseGlobalSearchReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  navigateToAdvancedSearch: (query: string) => void;
}

/**
 * Hook for global search dialog state and keyboard shortcuts
 * Listens for Cmd-K (Mac) or Ctrl-K (Windows/Linux) to open the search dialog
 */
export function useGlobalSearch(): UseGlobalSearchReturn {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const navigateToAdvancedSearch = useCallback((query: string) => {
    close();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  }, [close, navigate]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd-K (Mac) or Ctrl-K (Windows/Linux) opens search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        toggle();
        return;
      }

      // Escape closes search if open
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        close();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggle, close]);

  return {
    isOpen,
    open,
    close,
    toggle,
    navigateToAdvancedSearch,
  };
}
